import { logbookApi, type CreateDivePayload } from '@/api/logbook'
import { enqueue, allByKind, remove, countByKind } from './offlineQueue'

/**
 * Sincronizzazione offline-first delle immersioni registrate.
 *
 * Quando manca la rete (o la create fallisce per rete), l'immersione è messa in coda
 * su IndexedDB e sincronizzata appena si torna online. Il QuickLog diventa così
 * affidabile in barca, dove la connettività è assente: nessun dato perso.
 */

const KIND = 'dive-log'

/** Evento emesso quando la coda cambia (enqueue o sync), così la UI aggiorna il badge. */
export const OFFLINE_DIVES_CHANGED = 'edive:offline-dives-changed'

function emitChanged(): void {
  try { window.dispatchEvent(new CustomEvent(OFFLINE_DIVES_CHANGED)) } catch { /* ignore */ }
}

/** Mette un'immersione in coda per l'invio differito. */
export async function queueDiveLog(payload: CreateDivePayload): Promise<void> {
  await enqueue(KIND, payload)
  emitChanged()
}

/** Numero di immersioni in attesa di sincronizzazione (0 se IndexedDB non è disponibile). */
export async function pendingDiveLogCount(): Promise<number> {
  try { return await countByKind(KIND) } catch { return 0 }
}

let syncing = false

/** Svuota la coda inviando le immersioni. Si ferma al primo errore (rete di nuovo giù). */
export async function syncDiveLogs(): Promise<{ synced: number; failed: number }> {
  if (syncing || (typeof navigator !== 'undefined' && !navigator.onLine)) return { synced: 0, failed: 0 }
  syncing = true
  let synced = 0
  let failed = 0
  try {
    const items = await allByKind<CreateDivePayload>(KIND)
    for (const item of items) {
      try {
        await logbookApi.create(item.payload)
        await remove(item.id)
        synced++
      } catch {
        failed++
        break
      }
    }
  } catch {
    /* IndexedDB non disponibile: niente da sincronizzare */
  } finally {
    syncing = false
  }
  if (synced > 0) emitChanged()
  return { synced, failed }
}

let inited = false

/**
 * Inizializza la sincronizzazione (idempotente): drena la coda all'avvio e ogni volta
 * che la rete torna disponibile. `onSynced` riceve il numero di immersioni inviate.
 */
export function initDiveLogSync(onSynced?: (count: number) => void): void {
  if (inited || typeof window === 'undefined') return
  inited = true
  const run = () => { void syncDiveLogs().then(r => { if (r.synced > 0) onSynced?.(r.synced) }) }
  window.addEventListener('online', run)
  if (navigator.onLine) run()
}
